import { FC } from 'react'
import { graphql } from 'relay-runtime'
import { Post_post$key } from './__generated__/Post_post.graphql'
import { useFragment } from 'react-relay/hooks'
import dayjs from 'dayjs'
import Image from 'next/image'
import { PostContent } from '@lifedot/components/PostContent'
import { css } from '@emotion/react'
import { palette } from '@lifedot/styles/palette'
import { mq } from '@lifedot/styles/mediaQuery'
import { typography } from '@lifedot/styles/typography'
import { replacePostBody } from '@/utils/replacePostBody'
import { replaceShortCode } from '@/utils/replaceShortCode'
import { usePreview } from '@/hooks/use-preview'

const styles = {
  root: css({
    backgroundColor: palette.white
  }),
  header: css({
    padding: '20px 40px',
    borderBottom: `4px solid ${palette.main}`,
    [mq('sp')]: {
      padding: 12
    }
  }),
  img: css({
    position: 'relative',
    paddingTop: '56.27%'
  }),
  content: css({
    padding: '20px 40px',
    [mq('sp')]: {
      padding: 12
    }
  }),
  supplement: css({
    display: 'block',
    color: palette.gray6,
    textAlign: 'right'
  })
}

interface PostProps {
  post: Post_post$key | null
}

export const Post: FC<PostProps> = ({ post }) => {
  const data = useFragment(fragment, post)
  const preview = usePreview()
  if (!data) return null

  return (
    <article css={styles.root}>
      <header css={styles.header}>
        <h1 css={typography.headingXL}>{data.h1}</h1>
        <time
          dateTime={dayjs(data.modified_at).format('YYYY-MM-DD HH:mm:ss')}
          itemProp="dateModified"
          css={[typography.textXS, styles.supplement]}
        >
          更新日: {dayjs(data.modified_at).format('YYYY年M月D日')}
        </time>
      </header>
      {data.image && (
        <div css={styles.img}>
          <Image
            src={data.image.url}
            priority
            alt={data.image.alt || data.h1}
            layout="fill"
            objectFit="cover"
          />
        </div>
      )}
      <section css={styles.content}>
        <PostContent
          contentHtml={
            preview
              ? replacePostBody(
                  preview.body,
                  preview.shortcodes,
                  data.template_name
                )
              : replacePostBody(
                  data.content_text,
                  data.shortcodes,
                  data.template_name
                )
          }
          replace={replaceShortCode}
        />
      </section>
    </article>
  )
}

const fragment = graphql`
  fragment Post_post on Post {
    slug
    h1
    content_text
    template_name
    image {
      url
      alt
    }
    modified_at
    shortcodes {
      index
      category
      code
      params {
        key
        value
      }
    }
  }
`
